import { Box, Button, Grid, Paper, Typography, Stack, Divider, Link } from "@mui/material";
import { useMemo } from "react";
import { useDispatch } from "react-redux";
import {
  setApplicationStep,
  setUkRiskSummaryViewed,
  useOnboarding,
} from "src/store/features/onboarding.slice";
import api from "src/store/features/api";
import { useAuth } from "src/providers/AuthProvider";
import { ErrorOutlineRounded } from "@mui/icons-material";

const riskSummarySections = [
  {
    title: "1. You could lose all the money you invest",
    points: [
      "The performance of most cryptoassets can be highly volatile, with their value dropping as quickly as it can rise. You should be prepared to lose all the money you invest in cryptoassets.",
      "The cryptoasset market is largely unregulated. There is a risk of losing money or any cryptoassets you purchase due to risks such as cyber-attacks, financial crime and firm failure.",
    ],
  },
  {
    title: "2. You should not expect to be protected if something goes wrong",
    points: [
      "The Financial Services Compensation Scheme (FSCS) doesn't protect this type of investment because it's not a 'specified investment' under the UK regulatory regime — in other words, this type of investment isn't recognised as the sort of investment that the FSCS can protect.",
      "The Financial Ombudsman Service (FOS) will not be able to consider complaints related to this firm.",
    ],
  },
  {
    title: "3. You may not be able to sell your investment when you want to",
    points: [
      "There is no guarantee that investments in cryptoassets can be easily sold at any given time. The ability to sell a cryptoasset depends on various factors, including the supply and demand in the market at that time.",
      "Operational failings such as technology outages, cyber-attacks and comingling of funds could cause unwanted delay and you may be unable to sell your cryptoassets at the time you want.",
    ],
  },
  {
    title: "4. Cryptoasset investments can be complex",
    points: [
      "Investments in cryptoassets can be complex, making it difficult to understand the risks associated with the investment.",
      "You should do your own research before investing. If something sounds too good to be true, it probably is.",
    ],
  },
  {
    title: "5. Don't put all your eggs in one basket",
    points: [
      "Putting all your money into a single type of investment is risky. Spreading your money across different investments makes you less dependent on any one to do well.",
      "A good rule of thumb is not to invest more than 10% of your money in high-risk investments.",
    ],
  },
];

const hnwRiskSummarySection = {
  title: "6. High Net Worth Investor",
  points: [
    "Being a high-net worth investor will expose me to promotions for investment where there is a significant risk of losing all the money I invest. I am aware that it is open to me to seek professional advice before making any investment in a high-risk investment.",
  ],
};


interface UKRiskSummaryProps {
  // When provided, the component runs in public (token-only) mode: it skips the
  // authenticated API call and Redux step dispatch and defers navigation to the
  // parent instead.
  onPublicContinue?: () => void;
  // First name resolved from the public assessment link token — the greeting
  // has no authenticated user to read it from in public mode.
  publicFirstName?: string | null;
}

export default function UKRiskSummary({
  onPublicContinue,
  publicFirstName,
}: UKRiskSummaryProps = {}) {
  const dispatch = useDispatch();
  const { firstName, ukOnboarding } = useOnboarding();
  const { isAuthenticated } = useAuth();
  // Guard the user-data fetch behind auth — firing this pre-login has
  // previously caused a loading/redirect loop.
  const { data: userData } = api.useGetUserDatav2Query(undefined, { skip: !isAuthenticated });
  const [markRiskSummaryViewed] = api.useMarkRiskSummaryViewedMutation();

  const sections = useMemo(() => {
    const isHnwInvestor =
      ukOnboarding.investorType === "HIGH_NET_WORTH" &&
      ukOnboarding.hnwStatement.declarationAgreed;

    return isHnwInvestor
      ? [...riskSummarySections, hnwRiskSummarySection]
      : riskSummarySections;
  }, [ukOnboarding.investorType, ukOnboarding.hnwStatement.declarationAgreed]);

  const handleContinue = async () => {
    if (onPublicContinue) {
      onPublicContinue();
      return;
    }
    try {
      await markRiskSummaryViewed().unwrap();
      dispatch(setUkRiskSummaryViewed(true));
      dispatch(setApplicationStep("ukInvestorType"));
    } catch (error) {
      console.error("Failed to mark risk summary as viewed:", error);
      // Allow user to continue even if API fails
      dispatch(setUkRiskSummaryViewed(true));
      dispatch(setApplicationStep("ukInvestorType"));
    }
  };

  const handleExit = () => {
    window.location.href = "/";
  };

  const displayName = publicFirstName || firstName || userData?.details?.firstName || "there";

  return (
    <Grid container spacing={3} mt={2}>
      <Grid item xs={12}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            backgroundColor: "#FFF4E5",
            border: "1px solid #FFB74D",
            borderRadius: 2,
          }}
        >
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <ErrorOutlineRounded sx={{ color: "#F57C00", fontSize: 32 }} />
            <Box>
              <Typography variant="h5" fontWeight={600} gutterBottom>
                Risk Warning
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Hi {displayName}, this is a high-risk investment. How would you feel if you lost the
                money you're about to invest? Take 2 mins to learn more. For further information
                about cryptoassets, visit the FCA's website{" "}
                <Link
                  href="https://www.fca.org.uk/investsmart/crypto-basics"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  here
                </Link>
                .
              </Typography>
            </Box>
          </Stack>
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Risk Summary
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Estimated reading time: 2 min
          </Typography>
          <Typography variant="body1" paragraph>
            Due to the potential for losses, the Financial Conduct Authority (FCA) considers this
            investment to be high risk.
          </Typography>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            What are the key risks?
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Stack spacing={3}>
            {sections.map((section, index) => (
              <Box key={index}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  {section.title}
                </Typography>
                <Stack spacing={1} ml={2}>
                  {section.points.map((point, pointIndex) => (
                    <Typography key={pointIndex} variant="body2" color="text.secondary">
                      • {point}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            ))}
          </Stack>
          <Divider sx={{ my: 2 }} />
          <Typography variant="body2" color="text.secondary">
            If you are interested in learning more about how to protect yourself, visit the{" "}
            <Link
              href="https://www.fca.org.uk/investsmart"
              target="_blank"
              rel="noopener noreferrer"
            >
              FCA's website
            </Link>
            .
          </Typography>
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            backgroundColor: "#E3F2FD",
            border: "1px solid #90CAF9",
            borderRadius: 2,
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <ErrorOutlineRounded sx={{ color: "#1976D2", fontSize: 24, flexShrink: 0, mt: "2px" }} />
            <Stack spacing={2}>
              <Typography variant="body2">
                1. Once your application has been approved, there will be a 24 hour cooling off
                period before you can deposit or trade crypto.
              </Typography>
              <Typography variant="body2">
                2. By clicking "Continue", you acknowledge that you have read and understood the risk
                warnings above. You understand that cryptoassets are high-risk investments and that
                you could lose all the money you invest.
              </Typography>
            </Stack>
          </Stack>
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          justifyContent="center"
          mt={2}
          mb={4}
        >
          <Button
            variant="outlined"
            color="inherit"
            size="large"
            onClick={handleExit}
            sx={{ minWidth: 200 }}
          >
            Exit Application
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleContinue}
            sx={{ minWidth: 200 }}
          >
            I Understand, Continue
          </Button>
        </Stack>
      </Grid>
    </Grid>
  );
}
